/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { DocumentNode, GraphQLError } from 'graphql';
import { FetchPolicy, QueryOptions, useApolloClient } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import * as K from 'common/constants';
import { generateInsecureHash } from 'common/util/string';
import {
  DashboardPlotQueryInput,
  DashboardQueryInput,
  DashboardQueryOrigin,
  LogEntryEdge,
  PlotQueryInput,
  QueryOrigin,
  QueryRequestInput,
  QueryRequestResult,
  QueryType,
  SortOrder,
} from 'common/graphqlTypes';
import { createSearchGraphExpression, isComplexGraphingExpression } from 'common/util/graph';
import { addMillisecondsToTimestamp, parseDuration } from 'common/util/time';
import { addTermToFilter, simplifyFilter } from 'common/util/filter/filter';
import { REMOVE_LRQ } from 'components/common/Managers/LRQRequestManager.query';
import {
  Dashboard,
  GraphConfigType,
  GraphPlotConfigType,
} from 'components/dashboards/types';
import { calcGraphSlicing, getType, quoteParamValue } from 'components/dashboards/dashboardData';
import { LRQId } from 'components/dashboards/DashboardPage/DashboardRequestManager';
import {
  EventQueryFragment,
  generateLaunchDashboardQuery,
  generateLaunchQuery,
  generatePingQuery,
  generateQueriesResultNode,
  GRAPH_RESULT_FRAGMENT,
  HISTOGRAM_GRAPH_RESULT_FRAGMENT,
  LOG_ENTRY_FRAGMENT,
  POWER_QUERY_FRAGMENT,
  TOP_FACETS_RESULT_FRAGMENT,
} from './useLRQ.queries';
import {
  TEST_INTERVAL_MAX,
  TEST_INTERVAL_START,
  TEST_INTERVAL_STEP,
} from './useLRQ.test.settings';

import type { LRQRequest } from './useLRQ.types';

export type { LRQRequest };

// TODO: Simplified for now, pending schema-generated types
export type BaseResult = {
  status: string;
  terminationReasons?: Array<string>;
  terminationReasonsDetails?: Record<string, string>[][];
};

export type ServerError = GraphQLError & {
  details: Record<string, string>[];
};

interface QueriesResult extends BaseResult {
  results: Array<any>;
  token: string;
  stepsCompleted: number;
  totalSteps: number;
}

export type SuccessResult = QueriesResult;
export type ErrorResult = BaseResult;
export type LRQResult = SuccessResult | ErrorResult;

/**
 * When tests run:
 *  1. pingRequest() timing reduced to 5ms, 6ms, 7ms … (+1ms)
 *  2. Actual interval value returned with the response for the assertion
 */
const __TEST__ = process.env.NODE_ENV === 'test';

/**
 *  pingRequest() timing settings
 */
let INTERVAL_START = 100;
let INTERVAL_STEP = 50;
let INTERVAL_MAX = 1000;
if (__TEST__) {
  INTERVAL_START = TEST_INTERVAL_START;
  INTERVAL_STEP = TEST_INTERVAL_STEP;
  INTERVAL_MAX = TEST_INTERVAL_MAX;
}

export type LRQRequestSettings = {
  launchDashboardQuery?: boolean;
  fetchPolicy?: FetchPolicy;
  skip?: boolean; // If skip is true, the query will be skipped entirely | leveraging pattern looked up in Apollo https://www.apollographql.com/docs/react/api/react/hooks/#options
};

/**
 * Defines LaunchRequest object structure that's expected as an input to the {@link launchLRQ} function that makes
 * the actual {@link ApolloClient.query} call
 *
 * @param query            GraphQL query generated by {@link generateLaunchQuery}
 * @param variables        variables expected as an input for the {@link ApolloClient.query} call
 */
export type LaunchRequest = {
  query: DocumentNode;
  variables: { queryGroupRequest?: { queries: Array<{}>, preFilter?: string }; dashboardQuery?: { queries: Array<{}>, preFilter?: string, activeTab?: string} }; // eslint-disable-line no-undef
};

/**
 * Defines PingRequest object structure that's expected as an input to the {@link pingLRQ} function that makes the
 * actual {@link ApolloClient.query} call
 *
 * @param query            GraphQL query generated by {@link generatePingQuery}
 * @param variables        variables expected as an input for the {@link ApolloClient.query} call
 */
export type PingRequest = {
  query: DocumentNode;
  variables: {
    ids: string[];
    lastStepSeen: number;
    token: string;
  };
};

enum ClientStatus {
  ACTIVE    = 'ACTIVE',
  NONACTIVE = 'NONACTIVE',
}

export type UseLRQReturnValue = {
  result: LRQResult;
  cancel: () => void;
};

const DEFAULT_LRQ_REQUEST_SETTINGS: LRQRequestSettings = { launchDashboardQuery: false, fetchPolicy: 'network-only', skip: false };
export const INITIAL_RESULT = {} as LRQResult;

/**
 * @description Launches a group of potentially long-running event queries. The back-end will decide whether to use LRQ.
 * If all the results aren't available in the initial response, we will get back a token that can be used to ask for
 * more results. If we get a token and {@link QueriesResult.status} is not status 'DONE', we will poll periodically by
 * calling {@link pingQuery}. Each ping may or may not return more results.
 *
 * TODO: These values do not correctly match the possible values of the server enum that this field is based on
 * {@link QueriesResult.status}:
 *    'RUNNING':      In progress, individual event query results may have some or all data {@see QueryRequestResult}
 *    'TERMINATED':   Query group hasn't been resolved successfully (due to server or network issue), individual
 *                    event query results may have some or all data
 *    'DONE':         Query group resolved, all included event queries are complete:
 *                    {@link QueryRequestResult.stepsCompleted} === {@link QueryRequestResult.totalSteps}
 *
 * @param request        Request object to be processed. To perform 're-fetch' (while status is 'RUNNING' or 'DONE'),
 *                       supply a new object (so Object.is(prevRequest, request) !== true) - the effect will be cleaned
 *                       up and the process restarted
 */
function useLRQ(request: LRQRequest, requestSettings: LRQRequestSettings = {}): UseLRQReturnValue {
  requestSettings = { ...DEFAULT_LRQ_REQUEST_SETTINGS, ...requestSettings };

  /**
   * {@link result} represents the result of the submitted {@link LRQRequest}
   *    1. initially empty
   *    2. updated on launchLRQ()
   *    3. updated on every pingLRQ() request until all the data is resolved or an error occurred
   */
  const [result, setResult] = useState(INITIAL_RESULT);
  /**
   * {@link requestStatusMap} is used to track whether requests supplied to the hook are ACTIVE or INACTIVE.
   * Tracking this allows us to:
   *      1. avoid calling setState from an unmounted component
   *      2. prevent the callbacks for an INACTIVE request from updating the state and issuing new requests
   *      3. call removeRequest for INACTIVE requests
   */
  const requestStatusMap = useRef<Map<LRQRequest, ClientStatus>>(new Map());
  const interval = useRef(INTERVAL_START);
  const client = useApolloClient();

  useEffect(fetchDataInChunks, [request]);

  return { result, cancel };

  function fetchDataInChunks(): () => void {
    if (requestSettings.skip) {
      return () => {}; // do not launch LRQ
    }

    runEffect();

    return cleanup;

    async function runEffect(): Promise<any> {
      requestStatusMap.current.set(request, ClientStatus.ACTIVE);

      // 1. Launch LRQ
      const launchRequest = generateLaunchRequest(request, requestSettings.launchDashboardQuery || false);
      const launchResult = await launchLRQ(launchRequest);
      const launchQuery = requestSettings.launchDashboardQuery ? launchResult.data?.launchDashboardQuery : launchResult.data?.launchQuery;

      // 2. Update state from launchResult, but only if this request is still active
      if (requestStatusMap.current.get(request) === ClientStatus.ACTIVE) {

        /* Return current incomplete data in case of non server-related error */
        if (launchResult.errors) {
          const errorResult: ErrorResult = {
            status: K.QueryStatus.Terminated,
            terminationReasons: launchResult.errors.map((err) => (err.message)),
          };
          const errorResultDetails = launchResult.errors.map((err) => ((err as ServerError)?.details));
          if (errorResultDetails?.[0]) {
            errorResult.terminationReasonsDetails = errorResultDetails;
          }
          setResult(errorResult);
          // For the error case, always update the client request status to nonactive
          requestStatusMap.current.set(request, ClientStatus.NONACTIVE);
        } else {
          setResult(launchQuery as SuccessResult);
          if ([K.QueryStatus.Done, K.QueryStatus.Terminated].includes(launchQuery?.status)) {
            // For the success case, only set the status to nonactive if the LRQ is complete
            requestStatusMap.current.set(request, ClientStatus.NONACTIVE);
          }
        }
      }

      // 3. Remove the request and exit early if the request is no longer active
      if (requestStatusMap.current.get(request) !== ClientStatus.ACTIVE) {
        removeRequest(request, launchQuery?.token);
        return;
      }

      // 4. Ping until all the data is resolved
      const { results, stepsCompleted, token } = launchQuery || {};

      await fetchPerChunk();

      async function fetchPerChunk(): Promise<any> {
        return new Promise<void>((resolve) => {
          /* Initial ping() call, starting the recursion */
          setTimeout(
            ping,
            interval.current,
            generatePingRequest(
              request.queriesResultNode,
              getIdsWithoutErrors(results),
              stepsCompleted,
              token,
            ),
          );
          // eslint-disable-next-line consistent-return
          async function ping(pingRequest: PingRequest): Promise<any> {

            // 0. Remove the request and exit early if the request is no longer active.
            //    We make this check here as well because the situation may have changed during the timeout.
            if (requestStatusMap.current.get(request) !== ClientStatus.ACTIVE) {
              removeRequest(request, token);
              return resolve();
            }

            // 1. Ping LRQ
            const pingResult = await pingLRQ(pingRequest);

            // 2. Update state from pingResult, but only if this request is still active
            if (requestStatusMap.current.get(request) === ClientStatus.ACTIVE) {

              /* Return current incomplete data in case of non server-related error */
              if (pingResult.errors) {
                saveCurrentProgress(pingResult.errors.map((err) => err.message), K.QueryStatus.Terminated);
                // For the error case, always update the client request status to nonactive
                requestStatusMap.current.set(request, ClientStatus.NONACTIVE);
              } else {
                pingRequest.variables.ids = getIdsWithoutErrors(pingResult.data.pingQuery.results);
                pingRequest.variables.lastStepSeen = pingResult.data.pingQuery.stepsCompleted;
                if (__TEST__) {
                  pingResult.data.pingQuery.intervalUsed = interval.current;
                }
                setResult(pingResult.data.pingQuery as SuccessResult);

                if ([K.QueryStatus.Done, K.QueryStatus.Terminated].includes(pingResult.data.pingQuery.status)) {
                  // For the success case, only set the status to nonactive if the LRQ is complete
                  requestStatusMap.current.set(request, ClientStatus.NONACTIVE);
                }
              }
            }

            // 3. Remove the request and exit early if the request is no longer active
            if (requestStatusMap.current.get(request) !== ClientStatus.ACTIVE) {
              removeRequest(request, token);
              return resolve();
            }

            // 4. Ping LRQ
            increaseTimeout();
            setTimeout(
              ping,
              interval.current,
              pingRequest,
            );
          }
        });
      }
    }

    async function launchLRQ({ query, variables }: LaunchRequest) {
      const options: QueryOptions = {
        query,
        variables,
        fetchPolicy: requestSettings.fetchPolicy,
        errorPolicy: 'all',
      };
      // TODO: Revisit why fetchPolicy: 'network-only' breaks tests
      if (__TEST__) {
        delete options.fetchPolicy;
      }
      return client.query(options);
    }

    async function pingLRQ({ query, variables }: PingRequest) {
      const options: QueryOptions = {
        query,
        variables,
        fetchPolicy: requestSettings.fetchPolicy,
        errorPolicy: 'all',
      };
      // TODO: Revisit why fetchPolicy: 'network-only' breaks tests
      if (__TEST__) {
        delete options.fetchPolicy;
      }
      return client.query(options);
    }

    function increaseTimeout(): void {
      if (interval.current === INTERVAL_MAX) {
        return;
      }

      interval.current += INTERVAL_STEP;
    }
  }

  function cleanup(): void {
    requestStatusMap.current.set(request, ClientStatus.NONACTIVE);

    /* reset the state to initial when LRQ should re-run */
    interval.current = INTERVAL_START;
    /* TODO: I am not too sure about this. Is this step actually necessary? */
    setResult(INITIAL_RESULT);
  }

  function cancel(): void {
    /* Inform components subscribed to the request that it has been canceled */
    request.canceled = true;

    /* Stop tracking the LRQ on the client side */
    // TODO: Should we be doing this? Cleanup would happen later anyway when the request is replaced.
    // It seems that we DO need to set the request NONACTIVE, but we might not need the other state updates right now
    cleanup();
    // TODO: Is this state actually used anywhere?
    // This also creates a race condition where the setResult() in cleanup is immediately followed by the setResult()
    // in saveCurrentProgress
    saveCurrentProgress(['Canceled by client'], K.QueryStatus.Canceled);
  }

  /**
   * Called when non server-related error occurred (e.g. Network error)
   */
  function saveCurrentProgress(terminationReasons: Array<string> | undefined, status: string): void {
    // TODO 1 Discuss: we should probably return status per request
    //  e.g. Req1: Done, Req2: Done, Req3: Terminated
    //  In that case consuming UI can render Req1 & Req2 + render Req3, but mentioning that "data is incomplete"
    // TODO 2 if we go with "status per request": run through with Immer and set "Terminated" for the requests which are not "Done"
    // @see https://scalyr.myjetbrains.com/youtrack/issue/API-427
    setResult((currentState: LRQResult) => ({
      ...currentState,
      status,
      terminationReasons,
    }));
  }

  /**
   * removeRequest
   */
  function removeRequest(
    inputRequest: LRQRequest,
    token?: string,
  ): void {
    requestStatusMap.current.delete(inputRequest);

    if (token) {
      client.mutate({
        mutation: REMOVE_LRQ,
        variables: { token },
      });
    }
  }
}


/**
 * Get the ids from previously returned results, filtering out the results that included errors. This function is used
 * to supply ids as a parameter for {@link generatePingRequest} or to mutate the ids property of a ping request that is
 * in progress. The API contract requires that the ids for such queries are excluded from future pings.
 *
 * @param        results        List of {@link QueryRequestResult} returned by a launch or ping request
 *
 * @returns                     List of ids for the query results that did not include errors
 */
function getIdsWithoutErrors(results: QueryRequestResult[]): string[] {
  if (__TEST__) {
    return [];
  }
  return results.filter(({ error }) => error === null).map(({ id }) => id);
}

/**
 * Formats an {@link LRQRequest} for the {@link launchQuery} end point
 *
 * @param        queriesResultNode        DocumentNode representing the structure for the result
 * @param        queriesVariables         list of {@link QueryRequestInput} variables for each event query
 * @param        launchDashboardQuery     True if this should use the dedicated dashboard LRQ query interface
 * @param        preFilter                A string filter value to be piped in before all dashboard panels
 *
 * @returns                               a {@link LaunchRequest} to be supplied to the {@link launchQuery} end point
 */
export function generateLaunchRequest({
  queriesResultNode,
  queriesVariables,
  dashboardQueriesVariables,
  preFilter = '',
  activeTab = '',
}: LRQRequest,
launchDashboardQuery?: boolean): LaunchRequest {
  if (launchDashboardQuery) {
    const query = generateLaunchDashboardQuery(queriesResultNode);
    return {
      query,
      variables: { dashboardQuery: { queries: dashboardQueriesVariables || [], preFilter, activeTab } },
    };
  }
  const query = generateLaunchQuery(queriesResultNode);
  return {
    query,
    variables: { queryGroupRequest: { queries: queriesVariables || [], preFilter } },
  };
}

/**
 * Formats the inputs for the {@link pingQuery} end point
 *
 * @param        queriesResultNode        DocumentNode representing the structure for the result
 * @param        ids                      list of ids for the API to return for caching the results
 * @param        lastStepSeen             last seen stepsCompleted value from the server {@see QueryRequestResult}
 * @param        token                    unique token to associate this GraphQL request with its long-running operation
 *                                        on the server
 *
 * @returns                               a {@link PingRequest} to be supplied to the pingQuery end point
 */
export function generatePingRequest(
  queriesResultNode: DocumentNode,
  ids: string[],
  lastStepSeen: number,
  token: string,
): PingRequest {
  const query = generatePingQuery(queriesResultNode);

  return {
    query,
    variables: { ids, lastStepSeen, token },
  };
}

type LRQRequestParameters = {
  ascending?: boolean;
  breakdownFacet?: string;
  centered?: boolean;
  compareTimeOffset?: string;
  endTime: string;
  facet?: string;
  filter?: string;
  graphStyle?: string;
  numResults?: number;
  numBars?: number;
  offset?: string;
  origin?: QueryOrigin;
  plotQueries?: PlotQueryInput[];
  plotQuery?: PlotQueryInput;
  requestTime?: string;
  resultType?: string;
  showProgress?: boolean;
  slices?: number;
  sliceWidth?: string;
  sortOrder?: SortOrder;
  startTime: string;
  tabFilters?: string[];
  type?: QueryType;
  teamEmails?: string[];
};

/**
 * Generates a list of either one or two {@link LRQRequest} to be supplied to the {@link useLRQ} hook to represent a new
 * search on the search logs page. The first request is a query group with four event queries:
 *
 * An event query for the initial descending log lines for this search
 * An event query for the bar plot data on the timeline graph
 * An event query for the line plot data on the timeline graph
 * An event query for the top facets for this search
 *
 * For centered searches, a second request for ascending log lines will be generated as well, since it cannot be paired
 * with the descending query.
 *
 * @param        o.centered         if true, the query should be centered around some offset
 * @param        o.endTime          absolute end time for this search in milliseconds or nanoseconds
 * @param        o.filter           filter for this search
 * @param        o.offset           continuationToken for the event after which the server can continue returning data
 *                                  {@see LogEntryEdge}
 * @param        o.origin           provides the client context for this query to the server
 * @param        o.requestTime      time this search was generated in milliseconds
 * @param        o.startTime        absolute start time for this search in milliseconds or nanoseconds
 * @param        o.showProgress     indicates whether the LRQ should be represented with a LRQ Indication in the UI
 * @param        o.sortOrder        the sort order that will be used to display the events
 * @param        o.teamEmails       list of team email strings to use as a cross-team search
 *
 * @returns                       one or two {@link LRQRequest} to be supplied to the useLRQ hook
 */

export function generateNewSearchLogsRequests({
  centered = false,
  endTime,
  filter = '',
  offset = '',
  origin,
  requestTime,
  startTime,
  showProgress = false,
  sortOrder = K.DEFAULT_EVENT_SORT,
  teamEmails,
}: LRQRequestParameters): LRQRequest[] {

  const requests = [];
  requests.push({
    key: [centered, endTime, filter, offset, requestTime, startTime, '0'].join('|'),
    showProgress,
    queriesResultNode: generateQueriesResultNode([
      LOG_ENTRY_FRAGMENT,
      GRAPH_RESULT_FRAGMENT,
      GRAPH_RESULT_FRAGMENT,
      TOP_FACETS_RESULT_FRAGMENT,
    ]),
    queriesVariables: [
      generateEventQueryVariables({
        ascending: false,
        endTime,
        filter,
        numResults: K.NUM_INITIAL_LOG_ENTRIES,
        offset,
        origin,
        requestTime,
        startTime,
        teamEmails,
        type: QueryType.LOG,
      } as EventQueryInput),
      generateEventQueryVariables({
        endTime,
        origin,
        plotQuery: {
          expression: createSearchGraphExpression({ filter, countFunction: 'count' }),
          autoAlign: true,
          slices: K.NUM_BAR_TIMELINE_SLICES,
        },
        requestTime,
        startTime,
        teamEmails,
        type: QueryType.PLOT,
      } as EventQueryInput),
      generateEventQueryVariables({
        endTime,
        origin,
        plotQuery: {
          expression: createSearchGraphExpression({ filter }),
          autoAlign: true,
          slices: K.NUM_LINE_TIMELINE_SLICES,
        },
        requestTime,
        startTime,
        teamEmails,
        type: QueryType.PLOT,
      } as EventQueryInput),
      generateEventQueryVariables({
        endTime,
        facetQuery: {
          determineNumeric: true,
          includeSingleValueFacets: true,
          numFacetsToReturn: K.NUM_TOP_FACETS,
          numValuesToReturnPerFacet: K.NUM_VALUES_PER_FACET,
        },
        filter,
        origin,
        requestTime,
        startTime,
        teamEmails,
        type: QueryType.TOP_FACETS,
      } as EventQueryInput),
    ],
  });
  if (centered && !!offset) {
    const ascendingRequest = {
      key: [ centered, endTime, filter, offset, requestTime, startTime, '1' ].join('|'),
      queriesResultNode: generateQueriesResultNode([
        LOG_ENTRY_FRAGMENT,
      ]),
      queriesVariables: [
        generateEventQueryVariables({
          ascending: true,
          endTime,
          filter,
          numResults: K.NUM_INITIAL_LOG_ENTRIES,
          offset,
          origin,
          requestTime,
          startTime,
          teamEmails,
          type: QueryType.LOG,
        } as EventQueryInput),
      ],
    };
    // For centered searches, the ascending request may need to go before or after the descending request, depending on sort order
    if (sortOrder === SortOrder.MOST_RECENT_AT_TOP) {
      requests.unshift(ascendingRequest);
    } else {
      requests.push(ascendingRequest);
    }
  }
  return requests;
}

/**
 * Generates a list of {@link LRQRequest} to be supplied to the {@link useLRQ} hook to obtain the results
 * tabs count of results.
 *
 * Uses Plot Query as it's more accurate than a regular Event Query.
 *
 * @param        o.endTime          absolute end time for this search in milliseconds or nanoseconds
 * @param        o.filter           filter for this search
 * @param        o.requestTime      time this search was generated in milliseconds
 * @param        o.startTime        absolute start time for this search in milliseconds or nanoseconds
 * @param        o.showProgress     indicates whether the LRQ should be represented with a LRQ Indication in the UI
 * @param        o.teamEmails       list of team email strings to use as a cross-team search
 * @param        o.tabFilters       list of tab filters to use
 *
 * @returns
 */
export function generateNewResultsTabsRequest({
  endTime,
  filter = '',
  origin,
  requestTime,
  startTime,
  tabFilters = [],
  teamEmails,
}: LRQRequestParameters): LRQRequest {
  return {
    key: [endTime, filter, requestTime, startTime, '0'].join('|'),
    queriesResultNode: generateQueriesResultNode([GRAPH_RESULT_FRAGMENT]),
    queriesVariables: [
      ...tabFilters.map((tabFilter) =>
        generateEventQueryVariables({
          endTime,
          origin,
          plotQuery: {
            expression: createSearchGraphExpression({
              filter: addTermToFilter({ filter, text: tabFilter }),
              countFunction: 'count',
            }),
            slices: 1,
          },
          requestTime,
          startTime,
          teamEmails,
          type: QueryType.PLOT,
        } as EventQueryInput),
      ),
    ],
  };
}

/**
 * Generates an {@link LRQRequest} to be supplied to the {@link useLRQ} hook with a single request for retrieving log
 * lines. This function is used for continuation log line requests as well as for downloading log lines.
 *
 * @param        o.ascending          if true, fetch newer log lines from the offset
 * @param        o.endTime            absolute end time for this search in milliseconds or nanoseconds
 * @param        o.filter             filter for this search
 * @param        o.numResults         number of log lines to fetch
 * @param        o.offset             continuationToken for the log event after which the server can continue returning
 *                                    data {@see LogEntryEdge}
 * @param        o.origin             provides the client context for this query to the server
 * @param        o.requestTime        time this search was generated in milliseconds
 * @param        o.startTime          absolute start time for this search in milliseconds or nanoseconds
 * @param        o.teamEmails         list of team email strings to use as a cross-team search
 *
 * @returns                         an {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generateLogEntriesRequest({
  ascending,
  endTime,
  filter,
  numResults,
  offset,
  origin,
  requestTime,
  startTime,
  teamEmails,
}: LRQRequestParameters): LRQRequest {

  return {
    key: [ ascending, endTime, filter, numResults, offset, requestTime, startTime ].join('|'),
    queriesResultNode: generateQueriesResultNode([LOG_ENTRY_FRAGMENT]),
    queriesVariables: [
      generateEventQueryVariables({
        ascending,
        endTime,
        filter,
        numResults,
        offset,
        origin,
        requestTime,
        startTime,
        teamEmails,
        type: QueryType.LOG,
      } as EventQueryInput),
    ],
  };
}

// Number of milliseconds to add to (descending) or subtract from (ascending) the timestamp of the previous cursor
// when making continuation requests. Two was chosen because the loss of precision in 19-digit JavaScript numbers
// could cause unexpected "rounding".
const CURSOR_TIMESTAMP_BUFFER = 2;

/**
 * Generates an {@link LRQRequest} to be supplied to the {@link useLRQ} hook with a single request for retrieving log
 * lines. This function is used to generate a continuation LOG request when additional data is needed because the user
 * has scrolled up or down.
 *
 * LRQRequestParameters:
 * @param        o.endTime            absolute end time for this search in milliseconds or nanoseconds
 * @param        o.filter             filter for this search
 * @param        o.origin             provides the client context for this query to the server
 * @param        o.requestTime        time this search was generated in milliseconds
 * @param        o.startTime          absolute start time for this search in milliseconds or nanoseconds
 * @param        o.teamEmails         list of team email strings to use as a cross-team search
 *
 * Additional parameters:
 * @param        cursor             represents the edge in the previous request from which this request can continue
 * @param        scrollDirection    direction the user was scrolling when the continuation request was triggered
 * @param        sortOrder          the sort order that will be used to display the events
 *
 * @returns                         an {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generateContinuationRequest(
  {
    endTime,
    filter,
    origin,
    requestTime,
    startTime,
    teamEmails,
  }: LRQRequestParameters,
  cursor: LogEntryEdge,
  scrollDirection: K.SCROLL_DIRECTION,
  sortOrder: SortOrder,
): LRQRequest {

  const isScrollingUpTowardsNewerResults =
    scrollDirection === K.SCROLL_DIRECTION.UP
    && sortOrder === SortOrder.MOST_RECENT_AT_TOP;
  const isScrollingDownTowardsNewerResults =
    scrollDirection === K.SCROLL_DIRECTION.DOWN
    && sortOrder === SortOrder.MOST_RECENT_AT_BOTTOM;
  const ascending = isScrollingUpTowardsNewerResults || isScrollingDownTowardsNewerResults;

  // small optimization to help the server process continuation requests
  if (ascending) {
    // Subtract a small buffer from the timestamp of the previous cursor to use as the startTime of ascending queries
    startTime = addMillisecondsToTimestamp(cursor.logEntry.timestamp, -CURSOR_TIMESTAMP_BUFFER);
  }
  if (!ascending) {
    // Add a small buffer to the timestamp of the previous cursor to use as the endTime of descending queries
    endTime = addMillisecondsToTimestamp(cursor.logEntry.timestamp, CURSOR_TIMESTAMP_BUFFER);
  }
  return generateLogEntriesRequest({
    ascending,
    endTime,
    filter,
    numResults:  K.NUM_CONTINUATION_LOG_ENTRIES,
    offset:      cursor.continuationToken,
    origin,
    requestTime,
    startTime,
    teamEmails,
  });
}

/**
 * Utility function to determine the value of `autoAggregate` field within { @see generateGraphsPageRequests }
 */
function isAutoAggregate(
  facet: string | undefined,
  breakdownFacet: string | undefined,
  isComplexExpression: boolean,
  compareTimeOffset: string | undefined,
): boolean {
  return !!facet
         && facet !== K.RATE.op // this is to cover the `"View in Search"` feature (https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4721)
         && !breakdownFacet
         && !isComplexExpression
         && !compareTimeOffset;
}


/**
 * Generates a list of either one or two {@link LRQRequest} to be supplied to the {@link useLRQ} hook for a graph
 * displayed on the search graphs page.
 *
 * @param        o.breakdownFacet       breakdown facet for complex query expression
 * @param        o.compareTimeOffset    duration for a comparison graph
 * @param        o.endTime              absolute end time for this search in milliseconds or nanoseconds
 * @param        o.facet                facet for complex query expression
 * @param        o.filter               filter for this search
 * @param        o.graphStyle           disply style for this graph {@see CHART_TYPES}
 * @param        o.origin               provides the client context for this query to the server
 * @param        o.requestTime          time this search was generated in milliseconds
 * @param        o.slices               number of parts the time range will be divided into
 * @param        o.sliceWidth           a time value that determines the size of each slice
 * @param        o.startTime            absolute start time for this search in milliseconds or nanoseconds
 * @param        o.teamEmails           list of team email strings to use as a cross-team search
 *
 * @returns                           one or two {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generateGraphsPageRequests({
  breakdownFacet,
  compareTimeOffset,
  endTime,
  facet,
  filter = '',
  graphStyle,
  numBars,
  origin,
  requestTime,
  slices,
  sliceWidth,
  startTime,
  teamEmails,
}: LRQRequestParameters): LRQRequest[] {

  const isComplexExpression = isComplexGraphingExpression(filter);
  const expression = createSearchGraphExpression({
    filter,
    facet: facet || '',
    breakdownFacet,
    countFunction: graphStyle === K.CHART_TYPES.STACKED_BAR ? 'count' : 'countPerSec',
  });

  const requests = [];

  const plotRequest = generateEventQueryVariables({
    endTime,
    origin,
    plotQuery: {
      autoAggregate: isAutoAggregate(facet, breakdownFacet, isComplexExpression, compareTimeOffset),
      autoAlign: graphStyle === K.CHART_TYPES.STACKED_BAR && !breakdownFacet && !numBars,
      breakdownFacet,
      expression,
      slices,
      ...(sliceWidth && { sliceWidth }),
    },
    requestTime,
    startTime,
    teamEmails,
    type: breakdownFacet ? QueryType.BREAKDOWN : QueryType.PLOT,
  } as EventQueryInput);

  const facetsRequest = generateEventQueryVariables({
    endTime,
    facetQuery: {
      determineNumeric: true,
      includeSingleValueFacets: true,
      numFacetsToReturn: K.NUM_TOP_FACETS,
      numValuesToReturnPerFacet: K.NUM_VALUES_PER_FACET,
    },
    filter,
    origin,
    requestTime,
    startTime,
    teamEmails,
    type: QueryType.TOP_FACETS,
  } as EventQueryInput);

  if (breakdownFacet) {
    // Breakdown query must be in its own request
    requests.push({
      key: [ breakdownFacet, endTime, facet, filter, requestTime, startTime ].join('|'),
      queriesResultNode: generateQueriesResultNode([
        GRAPH_RESULT_FRAGMENT,
      ]),
      queriesVariables: [ plotRequest ],
    });
    if (!isComplexExpression) {
      requests.push({
        key: [ breakdownFacet, endTime, facet, filter, requestTime, startTime, QueryType.TOP_FACETS ].join('|'),
        queriesResultNode: generateQueriesResultNode([
          TOP_FACETS_RESULT_FRAGMENT,
        ]),
        queriesVariables: [ facetsRequest ],
      });
    }
  } else if (compareTimeOffset) {
    // For compare, we can bundle the first plot and the facets request, and then add the compare plot
    const compareTimeOffsetMS = parseDuration(compareTimeOffset);
    const comparisonStartTime = String(parseInt(startTime, 10) - compareTimeOffsetMS);
    const comparisonEndTime = String(parseInt(endTime, 10) - compareTimeOffsetMS);
    const compareTimeRequest = generateEventQueryVariables({
      endTime: comparisonEndTime,
      origin,
      plotQuery: {
        autoAlign: graphStyle === K.CHART_TYPES.STACKED_BAR && !breakdownFacet,
        expression,
        slices,
        ...(sliceWidth && { sliceWidth }),
      },
      requestTime,
      startTime: comparisonStartTime,
      teamEmails,
      type: QueryType.PLOT,
    } as EventQueryInput);

    const resultFragments = [GRAPH_RESULT_FRAGMENT];
    const queries = [plotRequest];
    if (!isComplexExpression) {
      resultFragments.push(TOP_FACETS_RESULT_FRAGMENT);
      queries.push(facetsRequest);
    }

    requests.push({
      key: [ endTime, facet, filter, requestTime, startTime ].join('|'),
      queriesResultNode: generateQueriesResultNode(resultFragments),
      queriesVariables: queries,
    });
    requests.push({
      key: [ endTime, facet, filter, requestTime, startTime, compareTimeOffset ].join('|'),
      queriesResultNode: generateQueriesResultNode([
        GRAPH_RESULT_FRAGMENT,
      ]),
      queriesVariables: [ compareTimeRequest ],
    });

  } else {
    // Regular graph - bundle the plot and facets queries into a single request
    const resultFragments = [GRAPH_RESULT_FRAGMENT];
    const queries = [plotRequest];
    if (!isComplexExpression) {
      resultFragments.push(TOP_FACETS_RESULT_FRAGMENT);
      queries.push(facetsRequest);
    }
    requests.push({
      key: [ breakdownFacet, endTime, facet, filter, requestTime, startTime ].join('|'),
      queriesResultNode: generateQueriesResultNode(resultFragments),
      queriesVariables: queries,
    });
  }

  return requests;
}

/**
 * Generates an {@link LRQRequest} to be supplied to the {@link useLRQ} hook for log volume graph displayed on the overview page
 * page.
 *
 * @param        eventParams        event params from location state
 *
 * @returns                         an {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generateLogVolumeGraphRequest(eventParams: QueryRequestInput): LRQRequest {
  const {
    startTime,
    endTime,
  } = eventParams;

  return {
    queriesResultNode: generateQueriesResultNode([GRAPH_RESULT_FRAGMENT]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        origin: QueryOrigin.OVERVIEW,
        plotQuery: {
          autoAlign: false,
          expression: 'sumPerSecond(value where $tag="logVolume" && $metric="logBytes")',
          slices: 480,
        },
        startTime,
        type: QueryType.PLOT,
      } as QueryRequestInput),
    ],
  };
}

/**
 * Generates an {@link LRQRequest} to be supplied to the {@link useLRQ} hook for getting more facet values with more accurate counts.
 *
 * @param o.endTime      absolute end time for this search in milliseconds or nanoseconds
 * @param o.facet        name of the facet to get values for
 * @param o.filter       filter for this search
 * @param o.origin       provides the client context for this query to the server
 * @param o.startTime    absolute start time for this search in milliseconds or nanoseconds
 * @param o.facets       an array of facets to get values for
 * @param o.teamEmails   an array of emails to search (CTS)
 *
 * @returns an {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generateFacetValuesRequest({
  endTime,
  facet,
  filter,
  origin,
  startTime,
  facets,
  teamEmails,
}: QueryRequestInput & {
  facets?: string[];
}): LRQRequest {

  if (facets) {
    const facetFragments = facets.map(() => TOP_FACETS_RESULT_FRAGMENT);
    const eventQueryVariables = facets.map((facetName) => generateEventQueryVariables({
      endTime,
      facetValuesQuery: {
        determineNumeric: true,
        name: facetName,
        numValuesToReturnForFacet: K.MORE_VALUES_PER_FACET,
      },
      filter,
      origin,
      startTime,
      teamEmails,
      type: QueryType.FACET_VALUES,
    } as QueryRequestInput));

    // sort facets for a unique key hash
    const sortedFacets = [...facets];
    sortedFacets.sort();

    return {
      key: [ startTime, endTime, filter, sortedFacets ].join('|'),
      queriesResultNode: generateQueriesResultNode(facetFragments),
      queriesVariables: eventQueryVariables,
    };
  }

  return {
    key: [startTime, endTime, filter, facet].join('|'),
    queriesResultNode: generateQueriesResultNode([TOP_FACETS_RESULT_FRAGMENT]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        facetValuesQuery: {
          determineNumeric: true,
          name: facet,
          numValuesToReturnForFacet: K.MORE_VALUES_PER_FACET,
        },
        filter,
        origin,
        startTime,
        teamEmails,
        type: QueryType.FACET_VALUES,
      } as QueryRequestInput),
    ],
  };
}

export interface EventQueryInput extends QueryRequestInput {
  requestTime?: string;
}

export interface DashboardQueryRequestInput extends DashboardQueryInput {
  requestTime?: string;
}

/**
 * Generates an {@link LRQRequest} to be supplied to the {@link useLRQ} hook for alert details time series graph displayed
 * on the details page
 *
 * @param        eventParams        event params from location state
 *
 * @returns                         an {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generateAlertTimeSeriesRequest(eventParams: QueryRequestInput): LRQRequest {
  const {
    startTime,
    endTime,
    plotQuery,
  } = eventParams;

  return {
    queriesResultNode: generateQueriesResultNode([GRAPH_RESULT_FRAGMENT]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        origin: QueryOrigin.ALERT,
        plotQuery,
        startTime,
        type: QueryType.PLOT,
      } as QueryRequestInput),
    ],
  };
}

/**
 * Takes most of the GraphQL input variables used for an event query along with the time that the request was generated
 * as input. The request time is used to help generate the unique client caching id for the query, but it isn't included
 * in the final query sent to the server. On the other hand, the origin parameter is passed along to the server, but is
 * not included in the unique id that is generated for client caching.
 *
 * @param        variables        collection of variables to be supplied to GraphQL, except for the unique id
 *
 * @returns                       collection of variables to be supplied to GraphQL
 */
export function generateEventQueryVariables(variables: EventQueryInput): QueryRequestInput {
  // request time is used to construct the id, but otherwise isn't included in the request.
  const { requestTime = '', origin = QueryOrigin.UNKNOWN, ...rest } = variables;
  return { origin, ...rest, id: JSON.stringify({ requestTime, ...rest }) };
}

/**
 * Takes most of the GraphQL input variables used for an dashboard query along with the time that the request was generated
 * as input. The request time is used to help generate the unique client caching id for the query, but it isn't included
 * in the final query sent to the server. On the other hand, the origin parameter is passed along to the server, but is
 * not included in the unique id that is generated for client caching.
 *
 * @param        variables        collection of variables to be supplied to GraphQL, except for the unique id
 *
 * @returns                       collection of variables to be supplied to GraphQL
 */
export function generateDashboardQueryVariables(variables: DashboardQueryRequestInput): DashboardQueryInput {
  // request time is used to construct the id, but otherwise isn't included in the request.
  const { requestTime = '', queryOrigin = DashboardQueryOrigin.DASHBOARD, ...rest } = variables;
  return { queryOrigin, ...rest, id: JSON.stringify({ requestTime, ...rest }) };
}

/**
 * Generates an {@link LRQRequest} to be supplied to the {@link useLRQ} hook for obtaining top facets
 *
 * @param        o.endTime          absolute end time for this search in milliseconds or nanoseconds
 * @param        o.origin           provides the client context for this query to the server
 * @param        o.requestTime      time this request was generated in milliseconds
 * @param        o.startTime        absolute start time for this search in milliseconds or nanoseconds
 *
 * @returns                       an {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generateTopFacetsRequest({
  endTime,
  origin,
  requestTime = '',
  startTime,
}: EventQueryInput): LRQRequest {
  return {
    key: [ startTime, endTime ].join('|'),
    queriesResultNode: generateQueriesResultNode([TOP_FACETS_RESULT_FRAGMENT]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        facetQuery: {
          determineNumeric: true,
          includeSingleValueFacets: true,
          numFacetsToReturn: K.NUM_TOP_FACETS,
          numValuesToReturnPerFacet: K.NUM_VALUES_PER_FACET,
        },
        filter: '',
        origin,
        requestTime,
        startTime,
        type: QueryType.TOP_FACETS,
      } as EventQueryInput),
    ],
  };
}

/**
 * Generates an {@link LRQRequest} to be supplied to the {@link useLRQ} hook for a Power Query
 *
 * @param        o.endTime          absolute end time for this search in milliseconds or nanoseconds
 * @param        o.filter           query for this PQ (uses the whole config for a REPORT)
 * @param        o.origin           provides the client context for this query to the server
 * @param        o.requestTime      time this search was generated in milliseconds
 * @param        o.resultType       'PLOT' for a PQ-based graph or undefined for a table
 * @param        o.startTime        absolute start time for this search in milliseconds or nanoseconds
 * @param        o.teamEmails       list of team email strings to use as a cross-team search
 * @param        o.type             QueryType.PQ for a standard PQ or QueryType.REPORT for a report as a PQ
 *
 * @returns                       an {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generatePQRequest({
  endTime,
  filter = '',
  origin,
  requestTime = '',
  resultType,
  startTime,
  teamEmails,
  type = QueryType.PQ,
}: LRQRequestParameters): LRQRequest {
  return {
    key: [endTime, filter, requestTime, startTime, type, resultType].join('|'),
    queriesResultNode: generateQueriesResultNode([resultType ? GRAPH_RESULT_FRAGMENT : POWER_QUERY_FRAGMENT]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        filter,
        origin,
        powerQuery: {
          resultType,
        },
        requestTime,
        startTime,
        teamEmails,
        type,
      } as EventQueryInput),
    ],
  };
}

/**
 * Generates two {@link LRQRequest} to be supplied to the {@link useLRQ} hook for a Power Query and top facets.
 * Commands are stripped from the filter used for the facets request.
 *
 * @param        o.endTime          absolute end time for this search in milliseconds or nanoseconds
 * @param        o.filter           query for this power query
 * @param        o.requestTime      time this search was generated in milliseconds
 * @param        o.resultType       'PLOT' for a PQ-based graph or undefined for a table
 * @param        o.startTime        absolute start time for this search in milliseconds or nanoseconds
 *
 * @returns                       two {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generatePQRequestWithFacets({
  endTime,
  filter = '',
  requestTime = '',
  resultType,
  startTime,
}: LRQRequestParameters): LRQRequest[] {

  const requests = [];
  requests.push({
    key: [endTime, filter, requestTime, startTime, resultType].join('|'),
    queriesResultNode: generateQueriesResultNode([resultType ? GRAPH_RESULT_FRAGMENT : POWER_QUERY_FRAGMENT]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        filter,
        powerQuery: {
          resultType,
        },
        requestTime,
        startTime,
        type: QueryType.PQ,
      } as EventQueryInput),
    ],
  });

  const simpleFilter = simplifyFilter(filter);
  requests.push({
    key: [ endTime, simpleFilter, requestTime, startTime, resultType,  QueryType.TOP_FACETS ].join('|'),
    queriesResultNode: generateQueriesResultNode([
      TOP_FACETS_RESULT_FRAGMENT,
    ]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        facetQuery: {
          determineNumeric: true,
          includeSingleValueFacets: true,
          numFacetsToReturn: K.NUM_TOP_FACETS,
          numValuesToReturnPerFacet: K.NUM_VALUES_PER_FACET,
        },
        filter: simpleFilter,
        requestTime,
        startTime,
        type: QueryType.TOP_FACETS,
      } as EventQueryInput),
    ],
  });

  return requests;
}

/**
 * Generates two {@link LRQRequest} to be supplied to the {@link useLRQ} hook for generating distribution graphs to
 * display on the histogram page.
 *
 * @param        o.endTime              absolute end time for this search in milliseconds or nanoseconds
 * @param        o.facet                facet for complex query expression
 * @param        o.filter               filter for this search
 * @param        o.requestTime          time this search was generated in milliseconds
 * @param        o.startTime            absolute start time for this search in milliseconds or nanoseconds
 * @param        o.teamEmails           list of team email strings to use as a cross-team search
 *
 * @returns                           two {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generateHistogramRequests({
  endTime,
  facet,
  filter,
  requestTime = '',
  startTime,
  teamEmails,
}: LRQRequestParameters): Array<LRQRequest> {
  return ([{
    key: [ endTime, facet, filter, requestTime, startTime, QueryType.TOP_FACETS ].join('|'),
    queriesResultNode: generateQueriesResultNode([
      TOP_FACETS_RESULT_FRAGMENT,
    ]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        facetQuery: {
          determineNumeric: true,
          includeSingleValueFacets: true,
          numFacetsToReturn: K.NUM_TOP_FACETS,
          numValuesToReturnPerFacet: K.NUM_VALUES_PER_FACET,
        },
        filter,
        requestTime,
        startTime,
        teamEmails,
        type: QueryType.TOP_FACETS,
      } as EventQueryInput),
    ],
  },
  {
    key: [ endTime, facet, requestTime, startTime ].join('|'),
    queriesResultNode: generateQueriesResultNode([HISTOGRAM_GRAPH_RESULT_FRAGMENT]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        facet,
        filter,
        requestTime,
        startTime,
        teamEmails,
        type: QueryType.DISTRIBUTION,
      } as EventQueryInput)],
  }]);
}

/**
 * Generates an {@link LRQRequest} to be supplied to the {@link useLRQ} hook for a dashboard Power Query
 *
 * @param        o.endTime          absolute end time for this search in milliseconds or nanoseconds
 * @param        o.origin           provides the client context for this query to the server
 * @param        o.plotQuery        the query for this breakdown
 * @param        o.startTime        absolute start time for this search in milliseconds or nanoseconds
 *
 * @returns                       an {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generateBreakdownRequest({
  endTime,
  origin,
  plotQuery,
  startTime,
}: LRQRequestParameters): LRQRequest {
  return {
    key: [ startTime, endTime, JSON.stringify(plotQuery) ].join('|'),
    queriesResultNode: generateQueriesResultNode([GRAPH_RESULT_FRAGMENT]),
    queriesVariables: [
      generateEventQueryVariables({
        endTime,
        origin,
        plotQuery,
        startTime,
        type: QueryType.BREAKDOWN,
      } as any as QueryRequestInput),
    ],
  };
}

/**
 * Generates an {@link LRQRequest} to be supplied to the {@link useLRQ} hook for a bundle of plots.
 *
 * @param        o.endTime          absolute end time for this search in milliseconds or nanoseconds
 * @param        o.origin           provides the client context for this query to the server
 * @param        o.plotQueries      an array of plot query structures
 * @param        o.startTime        absolute start time for this search in milliseconds or nanoseconds
 *
 * @returns                       an {@link LRQRequest} to be supplied to the useLRQ hook
 */
export function generatePlotsRequest({
  endTime,
  origin,
  plotQueries,
  startTime,
}: LRQRequestParameters): LRQRequest {
  const plotQueryVariables: QueryRequestInput[] = [];
  const plotFragments: EventQueryFragment[] = [];
  const queries = plotQueries as PlotQueryInput[];
  let requestKey = '';

  for (let plotIndex = 0; plotIndex < queries?.length; plotIndex += 1) {
    const queryVariables = generateEventQueryVariables({
      endTime,
      origin,
      plotQuery: queries[plotIndex],
      startTime,
      type: QueryType.PLOT,
    } as any as QueryRequestInput);
    requestKey += JSON.stringify(queryVariables);
    plotQueryVariables.push(queryVariables);
    plotFragments.push(GRAPH_RESULT_FRAGMENT);
  }

  return {
    key: [ startTime, endTime, generateInsecureHash(requestKey) ].join('|'),
    queriesResultNode: generateQueriesResultNode(plotFragments),
    queriesVariables: plotQueryVariables,
  };
}

/**
 * Creates a launchDashboard request for every widget and/or plot in the dashboard.
 *
 * TODO: As of 1/19/2021, Nothing is done with the result of this request. When actually hooking up the result of this request,
 * some of the logic here should maybe be extracted for better organization with dashboard utility functions. A lot of this
 * is very closely related to what is in DashboardRequestManager currently.
 *
 * @param startTime      The start time as an abbreviation or absolute time
 * @param endTime        The end time as an abbreviation or absolute time (may be an empty string for "now")
 * @param dashboard      The enclosing Dashboard object which contains the config
 * @param viewGraphIndex An optional graph index if we're only viewing one at a time
 * @param queryOrigin    Provides the client context for these queries to the server
 * @param requestTime    String representing the request time used for unique id generation
 * @param preFilter      A string filter value to be piped in before all dashboard panels

 */
export const generateLaunchDashboardRequests = (
  startTime: string,
  endTime: string,
  dashboard: Dashboard,
  viewGraphIndex: number,
  queryOrigin: DashboardQueryOrigin,
  requestTime: string,
  preFilter: string,
  activeTab?: string,
): { items: LRQId[]; requests: LRQRequest[] } => {
  const configs: GraphConfigType[] = dashboard.config.graphs as GraphConfigType[];
  const lrqItems: LRQId[] = [];
  const requests: LRQRequest[] = [];
  const plotQueries = []; // Batch our regular plot queries together to send as one request.
  let plotQueriesRequestKey = '';
  const parameters = {} as Record<string, string>;
  dashboard.params.forEach((param) => {
    if (param?.name && param?.value) {
      // Append quotes around parameter value in the case that it's a parameter that contains a facet
      parameters[param.name] = param.config.facet ? quoteParamValue(param.value) : param.value;
    }
  });
  // Then, we loop through all the graphs and generate requests and list of LRQId (for mapping to event cache)
  for (let graphIndex = 0; graphIndex < configs.length; graphIndex += 1) {
    if (viewGraphIndex < 0 || viewGraphIndex === graphIndex) { // only request data that is actually needed.
      const graphConfig = configs[graphIndex];
      const vizType = getType(graphConfig);
      const chartPlots = graphConfig.plots ? (graphConfig.plots as GraphPlotConfigType[]) : [{ facet: graphConfig.facet || '', filter: graphConfig.filter || '' }];
      const slicedPlotQuery = calcGraphSlicing(startTime, endTime, dashboard, graphIndex);
      const isPQ = vizType.isReport || vizType.isPowerQuery || vizType.isHoneyComb;
      if (isPQ || vizType.isDistribution) { // POWER QUERY, REPORTS, HISTOGRAM
        let resultNode;
        if (isPQ) {
          resultNode = vizType.isTimeseries ? GRAPH_RESULT_FRAGMENT : POWER_QUERY_FRAGMENT;
        } else {
          resultNode = HISTOGRAM_GRAPH_RESULT_FRAGMENT;
        }
        // Insure plotQuery input is present if we are planning on rendering PQ as timeseries graph
        const plotQuery = vizType.isTimeseries ? {
          ...slicedPlotQuery as unknown as DashboardPlotQueryInput,
          plotIndex: 0,
        } : undefined;
        const dashboardQueryVariables = generateDashboardQueryVariables({
          requestTime,
          startTime,
          endTime,
          queryOrigin,
          name: dashboard.config.name,
          index: graphIndex,
          plotQuery,
          parameters,
          teamEmails: graphConfig.teamEmails,
        } as DashboardQueryRequestInput);
        requests.push({
          key: [ startTime, endTime, generateInsecureHash(JSON.stringify(dashboardQueryVariables)) ].join('|'),
          queriesResultNode: generateQueriesResultNode([ resultNode ]),
          dashboardQueriesVariables: [dashboardQueryVariables],
          queriesVariables: [],
          preFilter,
          activeTab,
        });
        lrqItems.push({
          graphIndex,
          plotIndex: 0,
          id: dashboardQueryVariables.id,
        });
      } else if (graphConfig.breakdownFacet) { // BREAKDOWN graph
        const dashboardQueryVariables = generateDashboardQueryVariables({
          requestTime,
          startTime,
          endTime,
          queryOrigin,
          name: dashboard.config.name,
          index: graphIndex,
          plotQuery: {
            ...slicedPlotQuery as unknown as DashboardPlotQueryInput,
            plotIndex: 0,
            breakdownFacet: graphConfig.breakdownFacet,
          },
          parameters,
          teamEmails: graphConfig.teamEmails,
        } as DashboardQueryRequestInput);
        requests.push({
          key: [ startTime, endTime, generateInsecureHash(JSON.stringify(dashboardQueryVariables)) ].join('|'),
          queriesResultNode: generateQueriesResultNode([ GRAPH_RESULT_FRAGMENT ]),
          dashboardQueriesVariables: [dashboardQueryVariables],
          queriesVariables: [],
          preFilter,
          activeTab,
        });
        lrqItems.push({
          graphIndex,
          plotIndex: 0,
          id: dashboardQueryVariables.id,
        });
      } else if (chartPlots?.length) { // STANDARD OR MULTIPLOT GRAPH
        // TODO: Handle time shift with new API
        for (let plotIndex = 0; plotIndex < chartPlots?.length; plotIndex += 1) {
          const dashboardQueryVariables = generateDashboardQueryVariables({
            requestTime,
            startTime,
            endTime,
            queryOrigin,
            name: dashboard.config.name,
            index: graphIndex,
            plotQuery: {
              ...slicedPlotQuery as unknown as DashboardPlotQueryInput,
              plotIndex,
            },
            parameters,
            teamEmails: graphConfig.teamEmails,
          } as DashboardQueryRequestInput);
          // Don't push a full request, we batch all plots into one request later
          plotQueries.push(dashboardQueryVariables);
          lrqItems.push({
            graphIndex,
            plotIndex: 0,
            id: dashboardQueryVariables.id,
          });
          plotQueriesRequestKey += JSON.stringify(dashboardQueryVariables);
        }
      }
    }
  }

  // Batch all standard PLOT queries into one request.
  if (plotQueries.length > 0) {
    requests.push({
      key: [ startTime, endTime, generateInsecureHash(plotQueriesRequestKey) ].join('|'),
      queriesResultNode: generateQueriesResultNode([ GRAPH_RESULT_FRAGMENT ]),
      dashboardQueriesVariables: plotQueries,
      queriesVariables: [],
      preFilter,
      activeTab,
    });
  }

  return { items: lrqItems, requests };
};

export default useLRQ;
